/** @jsxImportSource solid-js */
import { createSignal, For } from "solid-js";
import { classes } from "#palette/style/index.solid";
import { Icons } from "../icons.solid";

export interface SelectItem {
  id: string;
  display: string;
}

export function createSelectInput({
  label = "",
  placeholder = "Please choose a value",
  items = [] as SelectItem[],
  initial = -1,
}) {
  let containerRef!: HTMLDivElement;
  const [selectedIndex, setSelectedIndex] = createSignal(initial);
  const [isOpen, setOpen] = createSignal(false);

  function openBox() {
    setOpen(true);
    const closeMe = (e: Event) => {
      if (isOpen() && !containerRef.contains(e.currentTarget)) {
        setOpen(false);
      }
    };

    document.body.addEventListener("click", closeMe);
  }

  return {
    component: (
      <div class="relative">
        <span class="block font-sans font-bold my-2">{label}</span>
        <div ref={containerRef}>
          <button
            class="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left shadow-sm hover:ring-1 ring-brand-300 border border-brand-500 focus:outline-none sm:text-sm/6"
            type="button"
            onClick={openBox}
          >
            <span class="block truncate">
              {items[selectedIndex()]?.display ?? (
                <span class="text-brand-300">{placeholder}</span>
              )}
            </span>
            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <Icons.ChevronUpDown class="w-5 h-5 weak" aria-hidden="true" />
            </span>
          </button>
          <ul
            class={classes(
              "absolute list-none border border-brand-500 shadow rounded bg-white w-full group z-50 mt-2 max-h-40 overflow-y-auto",
              isOpen() ? "block" : "hidden",
            )}
          >
            <For each={items}>
              {(i, idx) => {
                const clickThis = () => {
                  setSelectedIndex(idx());
                  setOpen(false);
                };
                return (
                  <li
                    class={classes(
                      "p-2 group w-full block hover:bg-brand-100 hover:text-brand-900 sm:text-sm/6",
                      idx() === selectedIndex()
                        ? "text-brand-900 bg-brand-100"
                        : "",
                    )}
                  >
                    <input
                      type="radio"
                      id={i.id}
                      name="__select_box_internal"
                      class="absolute top-0 opacity-0"
                    />
                    <label
                      for={i.id}
                      onClick={clickThis}
                      onKeyPress={clickThis}
                      class="w-full flex !m-0"
                    >
                      {i.display}
                      {idx() === selectedIndex() ? (
                        <div class="grow align-end justify-end text-right">
                          <Icons.OK />
                        </div>
                      ) : (
                        ""
                      )}
                    </label>
                  </li>
                );
              }}
            </For>
          </ul>
        </div>
      </div>
    ),
    selected: selectedIndex,
    setSelected: setSelectedIndex,
  };
}
